<template>
  <div class="holiday-setup">
    <div
      v-if="!editing"
      class="mb-15"
    >
      <ValidationProvider
        v-slot="{ errors }"
        mode="eager"
        rules="required"
        name="symbolic_type"
        :model-value="holiday.symbolic_type"
      >
        <div :class="{ 'has-error': errors[0] }">
          <label class="control-label required"> Holiday* </label>
          <AppSelect
            :value="holiday.symbolic_type"
            :options="holidays"
            :select2-options="holidaySelect2Options"
            data-testid="holiday-setup-form-type-select"
            @input="updateSymbolicType({ field: 'symbolic_type', value: $event })"
          />
          <span class="help-block">{{ errors[0] }}</span>
        </div>
      </ValidationProvider>
    </div>

    <div v-if="showHolidayFields">
      <div
        v-if="!editing"
        class="text-grey mb-15"
      >
        Choose how you want to set up holidays
      </div>

      <div
        v-if="holiday.past_holiday"
        class="dialog-warning blue mb-15"
      >
        <i class="d-flex align-items-center hi hi-info-filled text-blue hi-12"></i>
        <div>Past holidays cannot be fully edited. Only paid hours.</div>
      </div>
      <div class="d-flex flex-column flex-md-row mb-10">
        <ValidationProvider
          v-slot="{ errors }"
          mode="eager"
          rules="required|min:3|max:50"
          name="name"
          :model-value="holiday.name"
        >
          <div
            class="holiday-name"
            :class="{ 'has-error': errors[0] }"
          >
            <label class="control-label required">
              Holiday name
              <abbr title="required">*</abbr>
            </label>
            <AppInput
              :value="holiday.name"
              type="text"
              placeholder="Enter the holiday name"
              :disabled="disableHolidayNameInput"
              data-testid="holiday-name-input"
              @input="updateHoliday({ field: 'name', value: $event })"
            />
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>

        <ValidationProvider
          v-slot="{ errors }"
          mode="eager"
          name="amount_used"
        >
          <div
            :class="{ 'has-error': errors[0] }"
            class="holiday-amount-used"
          >
            <AppDurationInput
              :value="holiday.amount_used"
              with-seconds="never"
              label="Paid hours"
              placeholder="0:00"
              required
              validate-day
              data-testid="holiday-paid-hours-input"
              @input="updateHoliday({ field: 'amount_used', value: $event })"
            />
            <span class="help-block">{{ errors[0] }}</span>
          </div>
        </ValidationProvider>
      </div>

      <div class="d-flex flex-column flex-md-row">
        <div
          v-if="showHolidayDatePicker"
          class="d-flex holiday-datepicker"
        >
          <ValidationProvider
            v-slot="{ errors }"
            mode="eager"
            rules="required"
            name="date"
            :model-value="dateValue"
          >
            <div :class="{ 'has-error': errors[0] }">
              <label class="control-label required">
                Select date
                <abbr title="required">*</abbr>
              </label>
              <AppDatePicker
                :value="dateValue"
                input-test-id="holiday-date"
                type="daily"
                :min-date="minDate"
                :show-quick-pick="false"
                :disabled="disableHolidayDateInput"
                :current-zone="currentZone"
                @input="updateHoliday({ field: 'date', value: $event })"
              />
              <span class="help-block">{{ errors[0] }}</span>
            </div>
          </ValidationProvider>
        </div>
        <div class="checkbox occurs-anually">
          <AppSwitch
            :value="holiday.recurring"
            label="Occurs annually"
            class="cursor-pointer"
            :disabled="holiday.past_holiday"
            @change="handleRecurringField($event)"
          />
        </div>
      </div>
      <div
        v-if="showOverlapShiftWarning"
        class="dialog-warning orange mt-15"
      >
        <i class="d-flex align-items-center hi hi-info-filled hi-12"></i>
        <div>
          {{ overlapShiftText }}
        </div>
      </div>
      <div
        v-if="showNextOccurrenceCreationWarning"
        class="dialog-warning orange mt-15"
      >
        <i class="d-flex align-items-center hi hi-warning text-orange hi-12 mr-10"></i>
        <div>An occurrence for next year will be created since this date has already passed this year.</div>
      </div>
      <div
        v-if="showRecurringMessage"
        class="dialog-warning mt-15"
      >
        <i class="d-flex align-items-center hi hi-warning text-danger hi-12"></i>
        <div class="ml-10">The select holiday policy will no longer occur annually.</div>
      </div>
    </div>
  </div>
</template>

<script>
import { extend } from '~/utils/vee_validate_compat';
import { min, max } from '@vee-validate/rules';

extend('min', {
  ...min,
  message: 'is too short (minimum is three characters)',
});

extend('max', {
  ...max,
  message: 'is too long (maximum is fifty characters)',
});

export default {
  name: 'HolidaySetupForm',
  props: {
    predefinedHolidays: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    holiday: {
      type: Object,
      required: true,
    },
    organizationTz: {
      type: String,
      default: 'UTC',
    },
    editing: {
      type: Boolean,
      default: false,
    },
    originalRecurring: {
      type: Boolean,
      default: false,
    },
    overlapShiftCount: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    holidaySelect2Options: {
      multiple: false,
      width: '100%',
      placeholder: 'Select holiday',
    },
    showRecurringMessage: false,
  }),
  computed: {
    currentZone() {
      return { name: this.organizationTz };
    },
    minDate() {
      return this.editing ? this.currentDate : moment.tz(this.organizationTz).subtract(1, 'week');
    },
    showNextOccurrenceCreationWarning() {
      if (this.editing) {
        return false;
      }

      return this.holiday.recurring && this.pastDateSelected;
    },
    pastDateSelected() {
      return moment.tz(this.holiday.date, this.organizationTz).isBefore(moment().tz(this.organizationTz), 'day');
    },
    showHolidayFields() {
      return this.holiday.symbolic_type || this.editing;
    },
    holidays() {
      const result = Object.entries(this.predefinedHolidays).map(([groupName, holidays]) => ({
        text: groupName,
        children: holidays.map((holiday) => {
          const { symbolic_type, display_name, date, original_name } = holiday;

          return { id: symbolic_type, text: display_name, date, originalName: original_name };
        }),
      }));

      result.unshift({ text: 'Select holiday', children: [{ id: 'custom', text: 'Custom' }] });

      return result;
    },
    currentDate() {
      return moment.tz(this.organizationTz);
    },
    isCustomHoliday() {
      const symbolicType = this.holiday?.symbolic_type;

      return ['custom', 'custom_yearly'].includes(symbolicType);
    },
    disableHolidayNameInput() {
      return !this.isCustomHoliday || this.holiday.past_holiday;
    },
    disableHolidayDateInput() {
      return this.disableHolidayNameInput;
    },
    showHolidayDatePicker() {
      return this.isCustomHoliday || this.editing;
    },
    dateValue() {
      return this.editing ? moment(this.holiday.date) : this.holiday.date;
    },
    overlapShiftText() {
      return `This holiday overlaps with ${this.overlapShiftCount} ${this.shiftText}`;
    },
    shiftText() {
      return this.overlapShiftCount > 1 ? 'shifts' : 'shift';
    },
    showOverlapShiftWarning() {
      return this.overlapShiftCount > 0 && !this.loading;
    },
  },
  watch: {
    'holiday.date': function (newDate, oldDate) {
      this.updateAutoAdd();
    },
    'holiday.recurring': function (newRecurring, oldRecurring) {
      this.updateAutoAdd();
    },
  },
  methods: {
    updateAutoAdd() {
      if (this.pastDateSelected && !this.holiday.recurring) {
        return this.updateHoliday({ field: 'auto_add_new_users', value: false });
      }
    },
    updateSymbolicType({ field, value }) {
      this.updateHoliday({ field, value });

      let name = null;
      let date = this.currentDate;

      if (value !== 'custom') {
        const predefinedHoliday = this.holidays
          .flatMap((arr) => arr.children)
          .find((item) => item.id === this.holiday.symbolic_type);

        name = predefinedHoliday.originalName;
        date = predefinedHoliday.date;
      }

      this.updateHoliday({ field: 'name', value: name });
      this.updateHoliday({ field: 'date', value: date });
    },
    updateHoliday({ field, value }) {
      this.$emit('holiday-updated', field, value);
    },
    handleRecurringField(value) {
      this.updateHoliday({ field: 'recurring', value });

      if (this.editing && this.originalRecurring && this.holiday.recurring === false) {
        this.showRecurringMessage = true;
      } else {
        this.showRecurringMessage = false;
      }
    },
  },
};
</script>
